import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  /** Explanation shown in the popover. */
  text: string;
  /** Optional extra classes on the trigger wrapper (e.g. spacing tweaks). */
  className?: string;
}

/**
 * Small reusable info icon with a hover (desktop) / tap (mobile) popover. Drop it
 * next to a section heading: `<InfoTooltip text="…" />`.
 *
 * Slate + bronze, theme-aware (slate-* tokens adapt to the active theme). The
 * popover floats above siblings (z-50) and is positioned with `absolute`, so it
 * overflows the card rather than being clipped by its edges.
 */
export function InfoTooltip({ text, className }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  /** Horizontal nudge, in px, that keeps the popover inside the viewport. */
  const [shift, setShift] = useState(0);

  /**
   * Keep the popover on screen.
   *
   * It is centred on its icon, which is fine mid-page and clips badly near an
   * edge — a tooltip on the top-right stat card of the mobile 2x2 grid loses its
   * last few characters on every line. Measure once on open and nudge it back
   * inside; the shift resets on close so the next measurement starts from centre.
   *
   * useLayoutEffect rather than useEffect so the correction lands before paint
   * and the popover never visibly jumps.
   */
  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const el = popRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    if (r.right > window.innerWidth - margin) {
      setShift(window.innerWidth - margin - r.right);
    } else if (r.left < margin) {
      setShift(margin - r.left);
    }
  }, [open]);

  // Dismiss on outside tap/click or Escape (so tap-to-open closes on mobile).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className={`relative inline-flex ${className ?? ''}`}>
      <button
        type="button"
        aria-label="More information"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center text-slate-500 hover:text-[#b89968] focus:text-[#b89968] focus:outline-none transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {open && (
        <span
          role="tooltip"
          // `normal-case tracking-normal` because these sit next to uppercase,
          // letter-spaced stat labels and both properties INHERIT — without the
          // reset a tooltip renders as a wall of spaced-out shouting.
          ref={popRef}
          className="absolute left-1/2 top-full z-50 mt-2 w-64 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-slate-200 shadow-xl"
          style={{
            borderTopColor: 'rgba(184,153,104,0.55)',
            // Centring plus the edge correction, in one transform — a Tailwind
            // -translate-x-1/2 class would be overwritten by this style anyway.
            transform: `translateX(calc(-50% + ${shift}px))`,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
